import axios from 'axios';
import {
  CardResponse, DocumentFilters, DocumentsAccess, DocumentsMeta, DownloadedFile, Export1cSide, ExportCount, ExportFormat, ExportKind,
  ExportMode, ListResponse, ProductOption, SortState, WarningsResponse
} from './types';
import { filtersToParams } from './format';

/** Сервис документов: с портала запросы идут через основной бэкенд (/api/v2/documents → :3003) */
const BASE = '/api/v2/documents';

export interface ListQuery extends SortState {
  filters: DocumentFilters;
  page: number;
  limit: number;
  facets?: boolean;
  /** Повтор того же набора фильтров (страница, сортировка, лимит, «Обновить») - без записи в журнал действий */
  silent?: boolean;
}

/** Повторная загрузка того же набора фильтров в журнал действий не пишется */
export const silentParam = (silent: boolean): Record<string, string> => (silent ? { silent: '1' } : {});

/** Расширение по типу содержимого ответа: Content-Type доступен браузеру всегда, в отличие от Content-Disposition */
function extensionFrom(contentType: string, format: ExportFormat): string {
  if (/zip/i.test(contentType)) return 'zip';
  if (/spreadsheetml/i.test(contentType)) return 'xlsx';
  if (/csv/i.test(contentType)) return 'csv';
  return format;
}

/**
 * Имя файла из Content-Disposition: сначала русское (filename*=UTF-8''…), затем ASCII (filename="…").
 * Если браузер заголовок скрыл (запрос между разными адресами в dev), имя собирается по типу содержимого.
 */
function fileNameFrom(disposition: unknown, contentType: string, format: ExportFormat, fallback: string): string {
  const header = String(disposition || '');
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf) {
    try {
      return decodeURIComponent(utf[1].trim());
    } catch {
      // некорректная кодировка - берём ASCII-имя
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  if (plain) return plain[1];
  return `${fallback}_${new Date().toISOString().slice(0, 10)}.${extensionFrom(contentType, format)}`;
}

/** Файл выгрузки: сервер сам решает, один файл или ZIP; примечание (например, цены недоступны) - в X-Export-Warning */
async function downloadFile(url: string, params: Record<string, string>, format: ExportFormat, fallback: string, signal?: AbortSignal): Promise<DownloadedFile> {
  const res = await axios.get(url, {
    params,
    responseType: 'blob',
    // файл формируется минутами: общий таймаут axios здесь не нужен, обрыв - через signal
    timeout: 0,
    signal
  });
  const blob: Blob = res.data;
  return {
    blob,
    filename: fileNameFrom(res.headers['content-disposition'], blob.type || String(res.headers['content-type'] || ''), format, fallback),
    warning: res.headers['x-export-warning'] ? String(res.headers['x-export-warning']) : null
  };
}

export const documentsApi = {
  async meta(signal?: AbortSignal): Promise<DocumentsMeta & { success: boolean }> {
    const res = await axios.get(`${BASE}/meta`, { signal });
    return res.data;
  },

  async list(q: ListQuery, signal?: AbortSignal): Promise<ListResponse> {
    const params = {
      ...filtersToParams(q.filters),
      ...silentParam(Boolean(q.silent)),
      sortBy: q.sortBy,
      sortOrder: q.sortOrder,
      page: String(q.page),
      limit: String(q.limit),
      ...(q.facets ? { facets: '1' } : {})
    };
    const res = await axios.get(`${BASE}/`, { params, signal });
    return res.data;
  },

  /** Карточка; повторная загрузка после действия - silent (открытие уже записано) */
  async card(id: number, silent: boolean, signal?: AbortSignal): Promise<CardResponse> {
    const res = await axios.get(`${BASE}/${id}`, { params: silentParam(silent), signal });
    return res.data;
  },

  async retry(id: number): Promise<{ success: boolean; message: string; newStatus?: number; newStatusName?: string }> {
    const res = await axios.post(`${BASE}/${id}/retry`, {});
    return res.data;
  },

  async hide(id: number, hide: boolean): Promise<{ success: boolean; message: string; isHidden: boolean }> {
    const res = await axios.post(`${BASE}/${id}/hide`, { hide });
    return res.data;
  },

  async reject(id: number): Promise<{ success: boolean; message: string }> {
    const res = await axios.post(`${BASE}/${id}/reject`, {});
    return res.data;
  },

  async deleteDraft(id: number): Promise<{ success: boolean; message: string }> {
    const res = await axios.post(`${BASE}/${id}/delete`, {});
    return res.data;
  },

  async export1c(id: number, side: Export1cSide): Promise<{ success: boolean; message: string }> {
    const res = await axios.post(`${BASE}/${id}/export-1c`, { side });
    return res.data;
  },

  /** Выгрузка КМ или товарных позиций документа */
  async exportDocument(id: number, kind: ExportKind, format: ExportFormat): Promise<Blob> {
    const res = await axios.get(`${BASE}/${id}/export`, { params: { kind, format }, responseType: 'blob' });
    return res.data;
  },

  /** Подбор товаров номенклатуры для фильтра (только позиции из документов в зоне видимости оператора) */
  async products(q: string, signal?: AbortSignal): Promise<ProductOption[]> {
    const res = await axios.get(`${BASE}/products`, { params: { q }, signal });
    return res.data.products || [];
  },

  async warnings(silent: boolean, signal?: AbortSignal): Promise<WarningsResponse> {
    const res = await axios.get(`${BASE}/warnings`, { params: silentParam(silent), signal });
    return res.data;
  },

  async extend(ids: number[]): Promise<{ success: boolean; extended: number; skipped: number; message: string }> {
    const res = await axios.post(`${BASE}/extend`, { ids });
    return res.data;
  },

  /** Сколько строк и файлов получится при выгрузке списка по фильтрам; колонки - доступные по правам */
  async exportCount(mode: ExportMode, filters: DocumentFilters, signal?: AbortSignal): Promise<ExportCount> {
    const res = await axios.get(`${BASE}/export/count`, { params: { ...filtersToParams(filters), mode }, signal });
    return res.data;
  },

  /** Выгрузка списка: строка на документ или на позицию; по 100 000 строк в файле, несколько файлов - ZIP */
  exportFile(mode: ExportMode, filters: DocumentFilters, sort: SortState, format: ExportFormat, columns: string[], signal?: AbortSignal): Promise<DownloadedFile> {
    const params = {
      ...filtersToParams(filters), mode, format, columns: columns.join(','), sortBy: sort.sortBy, sortOrder: sort.sortOrder
    };
    return downloadFile(`${BASE}/export`, params, format, mode === 'items' ? 'documents_items' : 'documents', signal);
  },

  /** Выгрузка вкладки «Проблемные документы на контроле» (все документы зоны, без лимита экрана) */
  exportWarnings(format: ExportFormat, signal?: AbortSignal): Promise<DownloadedFile> {
    return downloadFile(`${BASE}/export/warnings`, { format }, format, 'documents_warnings', signal);
  }
};

export type { DocumentsAccess };
export const isCancelled = (err: unknown): boolean => axios.isCancel(err);

/** 403 - нет права (раздел, вкладка, аналитика или выгрузка) */
export const isForbidden = (err: unknown): boolean => (err as { response?: { status?: number } })?.response?.status === 403;
